/**
 * «Ресурсы» (Spec 018, part B): what the business uses — servers, domains,
 * repositories, bots, accounts — described once for any number of projects,
 * and who may do what with each. A card says where a secret lives and how to
 * get it, never the secret itself: the server refuses text that looks like one.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Plus } from "lucide-react";

import { VersionConflictError } from "@/api/client";
import { Projects, ResourceCards, Users } from "@/api/resources";
import type { AccessOut, ResourceKind, ResourceOut } from "@/api/types";
import { SecretsSection } from "@/components/Secrets";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { DateField, SelectField, TextArea, TextField } from "@/components/ui/Field";
import { Section } from "@/components/ui/Section";
import { useToast } from "@/components/ui/Toast";
import { Markdown } from "@/lib/markdown";

export const KIND_LABELS: Record<ResourceKind, string> = {
  server: "Сервер",
  domain: "Домен",
  repository: "Репозиторий",
  bot: "Бот",
  account: "Аккаунт",
  subscription: "Подписка",
  service: "Сервис",
  other: "Другое",
};

const KIND_OPTIONS = Object.entries(KIND_LABELS).map(([value, label]) => ({ value, label }));

const NO_SECRETS = "Секрет сюда не пишут: только где он лежит и как его получить.";

const day = (iso: string) => new Date(iso).toLocaleDateString("ru-RU");

/** «Костя», «Костя · только подключение «agent»», with the expiry if any. */
export function holderLabel(
  access: Pick<AccessOut, "user_name" | "token_name" | "expires_at">,
): string {
  const who = access.user_name ?? "Участник";
  const via = access.token_name ? ` · только подключение «${access.token_name}»` : "";
  const until = access.expires_at ? ` · до ${day(access.expires_at)}` : "";
  return `${who}${via}${until}`;
}

/** «Записал Костя» or «Записал агент Кости»: an agent's record says so. */
export function recordedBy(access: Pick<AccessOut, "granted_by_name" | "via_agent">): string {
  const who = access.granted_by_name ?? "участник";
  return access.via_agent ? `Записал агент (${who})` : `Записал ${who}`;
}

/** Where the resource is used: named projects, or the whole business. */
export function scopeLabel(projectIds: string[], titles: Map<string, string>): string {
  if (projectIds.length === 0) return "общий для всего бизнеса";
  return projectIds.map((id) => titles.get(id) ?? "проект").join(", ");
}

/** The record only says «отозван»; the access itself is revoked where it lives. */
export function revokeHint(revokeMd: string | null): string {
  return revokeMd
    ? `Запись отметит доступ отозванным. Сам доступ отзывают так: ${revokeMd}`
    : "Запись отметит доступ отозванным. Сам доступ отзывают там, где его выдали.";
}

const errorText = (e: unknown) => (e instanceof Error ? e.message : "Не удалось сохранить");

export function ResourcesSection({ title, projectId }: { title: string; projectId?: string }) {
  const [adding, setAdding] = useState(false);
  const resources = useQuery({
    queryKey: ["resources", projectId ?? "all"],
    queryFn: () => ResourceCards.list(projectId),
  });
  const projects = useQuery({ queryKey: ["projects"], queryFn: () => Projects.list() });
  const titles = new Map((projects.data ?? []).map((p) => [p.id, p.title] as const));
  const items = resources.data ?? [];

  return (
    <Section title={title}>
      {resources.isError && (
        <Card role="alert" className="space-y-2">
          <p>Не удалось загрузить ресурсы.</p>
          <Button size="compact" variant="ghost" onClick={() => void resources.refetch()}>
            Повторить
          </Button>
        </Card>
      )}
      {resources.isSuccess && items.length === 0 && !adding && (
        <p className="text-meta text-hint">
          Пока ничего не описано. Здесь — серверы, домены, репозитории, боты и аккаунты, которыми
          пользуется {projectId ? "проект" : "бизнес"}, и кто что с ними может делать.
        </p>
      )}
      {items.map((r) => (
        <ResourceCard key={r.id} resource={r} scope={scopeLabel(r.project_ids, titles)} />
      ))}
      {adding ? (
        <NewResource projectId={projectId} onDone={() => setAdding(false)} />
      ) : (
        <div>
          <Button size="compact" variant="ghost" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Описать ресурс
          </Button>
        </div>
      )}
    </Section>
  );
}

function ResourceCard({ resource, scope }: { resource: ResourceOut; scope: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [granting, setGranting] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [error, setError] = useState<string>();
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["resources"] }),
      qc.invalidateQueries({ queryKey: ["my-accesses"] }),
    ]);
  const archive = useMutation({
    mutationFn: () => ResourceCards.archive(resource.id),
    onSuccess: async () => {
      await refresh();
      toast.success("Ресурс убран в архив");
    },
    onError: (e) => setError(errorText(e)),
  });
  const people = [
    resource.owner_name && `Владелец: ${resource.owner_name}`,
    resource.responsible_name && `Отвечает: ${resource.responsible_name}`,
  ].filter(Boolean);

  return (
    <Card className="space-y-1">
      <p className="text-meta text-hint">
        {KIND_LABELS[resource.kind]} · {scope}
      </p>
      <p className="text-cell-title font-medium text-text">{resource.name}</p>
      {resource.purpose && <p className="text-body text-hint">{resource.purpose}</p>}
      {resource.address && <p className="break-all text-body">{resource.address}</p>}
      {people.length > 0 && <p className="text-meta text-hint">{people.join(" · ")}</p>}
      {resource.renew_at && (
        <p className="text-meta text-hint">Продлить до {day(resource.renew_at)}</p>
      )}
      {resource.instructions_md && (
        <Markdown className="text-body" text={resource.instructions_md} />
      )}
      {resource.accesses.map((a) => (
        <AccessRow key={a.id} resourceId={resource.id} access={a} onChanged={refresh} />
      ))}
      <SecretsSection resource={resource} />
      {granting && <NewAccess resourceId={resource.id} onDone={() => setGranting(false)} />}
      {error && (
        <p role="alert" className="text-meta text-dstr">
          {error}
        </p>
      )}
      {!granting && (
        <div className="flex flex-wrap gap-1">
          <Button size="compact" variant="ghost" onClick={() => setGranting(true)}>
            <KeyRound className="h-4 w-4" aria-hidden="true" />
            Записать доступ
          </Button>
          {archiving ? (
            <>
              {/* A destructive action gets a second tap, as with comments. */}
              <Button size="compact" variant="ghost" onClick={() => setArchiving(false)}>
                Отмена
              </Button>
              <Button
                size="compact"
                variant="destructive-outline"
                loading={archive.isPending}
                onClick={() => archive.mutate()}
              >
                Точно в архив
              </Button>
            </>
          ) : (
            <Button
              size="compact"
              variant="ghost"
              className="text-dstr"
              onClick={() => setArchiving(true)}
            >
              В архив
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}

function AccessRow({
  resourceId,
  access,
  onChanged,
}: {
  resourceId: string;
  access: AccessOut;
  onChanged: () => Promise<unknown>;
}) {
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string>();
  const conflict = (e: unknown, what: string) => {
    if (e instanceof VersionConflictError) {
      // Someone changed the record: show the new one before asking again.
      setConfirming(false);
      setWithdrawing(false);
      setError(`Запись о доступе изменили. Перечитай её и ${what} снова, если нужно.`);
      void onChanged();
    } else setError(errorText(e));
  };
  const withdraw = useMutation({
    mutationFn: () =>
      ResourceCards.withdraw(resourceId, access.id, access.version, reason.trim()),
    onSuccess: async () => {
      setWithdrawing(false);
      await onChanged();
      toast.success("Запись снята");
    },
    onError: (e) => conflict(e, "сними"),
  });
  const revoke = useMutation({
    mutationFn: () => ResourceCards.revoke(resourceId, access.id, access.version),
    onSuccess: async () => {
      setConfirming(false);
      await onChanged();
      toast.success("Отмечено: доступ отозван");
    },
    onError: (e) => conflict(e, "отзови"),
  });

  return (
    <div className="space-y-1 border-t border-sep pt-2">
      <p className="text-body">
        <span className="font-medium">{holderLabel(access)}:</span> {access.actions_md}
      </p>
      {access.how_to_get_md && (
        <p className="text-meta text-hint">Как получить: {access.how_to_get_md}</p>
      )}
      {access.revoke_md && <p className="text-meta text-hint">Как отозвать: {access.revoke_md}</p>}
      <p className="text-meta text-hint">
        {recordedBy(access)}, {day(access.granted_at)}
      </p>
      {access.confirmation_md && (
        <p className="text-meta text-hint">По словам: «{access.confirmation_md}»</p>
      )}
      {error && (
        <p role="alert" className="text-meta text-dstr">
          {error}
        </p>
      )}
      {confirming ? (
        <div className="space-y-1">
          <p className="text-meta text-hint">{revokeHint(access.revoke_md)}</p>
          <div className="flex gap-1">
            <Button size="compact" variant="ghost" onClick={() => setConfirming(false)}>
              Отмена
            </Button>
            <Button
              size="compact"
              variant="destructive-outline"
              loading={revoke.isPending}
              onClick={() => revoke.mutate()}
            >
              Отметить отозванным
            </Button>
          </div>
        </div>
      ) : withdrawing ? (
        <div className="space-y-1">
          <TextField
            label="Почему запись ошибочная"
            value={reason}
            maxLength={500}
            placeholder="Например: дубль другой записи"
            onChange={(e) => setReason(e.target.value)}
          />
          <p className="text-meta text-hint">
            Запись уйдёт из списков и останется в истории. Сам доступ не отзывается.
          </p>
          <div className="flex gap-1">
            <Button size="compact" variant="ghost" onClick={() => setWithdrawing(false)}>
              Отмена
            </Button>
            <Button
              size="compact"
              variant="destructive-outline"
              loading={withdraw.isPending}
              disabled={reason.trim().length < 3}
              onClick={() => withdraw.mutate()}
            >
              Снять запись
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-1">
          <Button
            size="compact"
            variant="ghost"
            onClick={() => {
              setError(undefined);
              setConfirming(true);
            }}
          >
            Отозвать
          </Button>
          <Button
            size="compact"
            variant="ghost"
            onClick={() => {
              setError(undefined);
              setReason("");
              setWithdrawing(true);
            }}
          >
            Снять как ошибочную
          </Button>
        </div>
      )}
    </div>
  );
}

function NewResource({ projectId, onDone }: { projectId?: string; onDone: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [kind, setKind] = useState("");
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [address, setAddress] = useState("");
  const [instructions, setInstructions] = useState("");
  const [error, setError] = useState<string>();
  const create = useMutation({
    mutationFn: () =>
      ResourceCards.create({
        kind: kind as ResourceKind,
        name: name.trim(),
        purpose: purpose.trim() || null,
        address: address.trim() || null,
        instructions_md: instructions.trim() || null,
        // From a project's page it serves that project; from «О бизнесе» — all.
        project_ids: projectId ? [projectId] : [],
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["resources"] });
      toast.success("Ресурс описан");
      onDone();
    },
    onError: (e) => setError(errorText(e)),
  });

  return (
    <Card className="space-y-2">
      <SelectField
        label="Что это"
        value={kind}
        onChange={setKind}
        options={KIND_OPTIONS}
        placeholder="Выбери вид"
      />
      <TextField
        label="Название"
        value={name}
        maxLength={200}
        placeholder="Например: VPS Timeweb"
        onChange={(e) => setName(e.target.value)}
      />
      <TextField
        label="Зачем нужен — необязательно"
        value={purpose}
        maxLength={4000}
        onChange={(e) => setPurpose(e.target.value)}
      />
      <TextField
        label="Адрес — необязательно"
        value={address}
        maxLength={1000}
        placeholder="IP и порт, домен или ссылка"
        onChange={(e) => setAddress(e.target.value)}
      />
      <TextArea
        label="Как пользоваться — необязательно"
        value={instructions}
        rows={4}
        maxLength={20000}
        onChange={(e) => setInstructions(e.target.value)}
      />
      <p className="text-meta text-hint">{NO_SECRETS}</p>
      {error && (
        <p role="alert" className="text-meta text-dstr">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <Button
          size="compact"
          loading={create.isPending}
          disabled={!kind || !name.trim()}
          onClick={() => {
            setError(undefined);
            create.mutate();
          }}
        >
          Сохранить
        </Button>
        <Button size="compact" variant="ghost" onClick={onDone}>
          Отмена
        </Button>
      </div>
    </Card>
  );
}

function NewAccess({ resourceId, onDone }: { resourceId: string; onDone: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const people = useQuery({ queryKey: ["users"], queryFn: Users.list });
  const [userId, setUserId] = useState("");
  const [actions, setActions] = useState("");
  const [howToGet, setHowToGet] = useState("");
  const [revoke, setRevoke] = useState("");
  const [expires, setExpires] = useState("");
  const [error, setError] = useState<string>();
  const grant = useMutation({
    mutationFn: () =>
      ResourceCards.grant(resourceId, {
        user_id: userId,
        actions_md: actions.trim(),
        how_to_get_md: howToGet.trim() || null,
        revoke_md: revoke.trim() || null,
        // The whole chosen day, in the person's own time zone.
        expires_at: expires ? new Date(`${expires}T23:59:59`).toISOString() : null,
      }),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["resources"] }),
        qc.invalidateQueries({ queryKey: ["my-accesses"] }),
      ]);
      toast.success("Доступ записан");
      onDone();
    },
    onError: (e) => setError(errorText(e)),
  });
  const options = (people.data ?? [])
    .filter((u) => u.is_active)
    .map((u) => ({ value: u.id, label: u.display_name }));

  return (
    <div className="space-y-2 border-t border-sep pt-2">
      <SelectField
        label="Кому"
        value={userId}
        onChange={setUserId}
        options={options}
        placeholder="Выбери участника"
      />
      <TextField
        label="Что может делать"
        value={actions}
        maxLength={4000}
        placeholder="Например: SSH, только выкладка prosto.task"
        onChange={(e) => setActions(e.target.value)}
      />
      <TextArea
        label="Как получить — порядок, не сам секрет"
        value={howToGet}
        rows={3}
        maxLength={4000}
        onChange={(e) => setHowToGet(e.target.value)}
      />
      <TextField
        label="Как отозвать — необязательно"
        value={revoke}
        maxLength={4000}
        onChange={(e) => setRevoke(e.target.value)}
      />
      <DateField
        label="До какого дня — необязательно"
        value={expires}
        onChange={setExpires}
        placeholder="Бессрочно"
      />
      <p className="text-meta text-hint">{NO_SECRETS}</p>
      {error && (
        <p role="alert" className="text-meta text-dstr">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <Button
          size="compact"
          loading={grant.isPending}
          disabled={!userId || !actions.trim()}
          onClick={() => {
            setError(undefined);
            grant.mutate();
          }}
        >
          Записать
        </Button>
        <Button size="compact" variant="ghost" onClick={onDone}>
          Отмена
        </Button>
      </div>
    </div>
  );
}

/** «Мои доступы»: what the signed-in person may use, and how to get it. */
export function MyAccesses() {
  const mine = useQuery({ queryKey: ["my-accesses"], queryFn: ResourceCards.mine });
  if (!mine.isSuccess || mine.data.length === 0) return null;
  return (
    <Section title="Мои доступы">
      {mine.data.map(({ access, resource }) => (
        <Card key={access.id} className="space-y-1">
          <p className="text-meta text-hint">{KIND_LABELS[resource.kind]}</p>
          <p className="text-cell-title font-medium text-text">{resource.name}</p>
          <p className="text-body">{access.actions_md}</p>
          {access.token_name && (
            <p className="text-meta text-hint">Только подключение «{access.token_name}»</p>
          )}
          {access.how_to_get_md && (
            <p className="text-meta text-hint">Как получить: {access.how_to_get_md}</p>
          )}
          {access.expires_at && (
            <p className="text-meta text-hint">До {day(access.expires_at)}</p>
          )}
        </Card>
      ))}
    </Section>
  );
}
